import { AppStatType, DataPeriod } from '@prisma/client';
import { useQuery } from '@tanstack/react-query';
import { UserReportResponse } from '@zond/api-interfaces';
import { AxiosError } from 'axios';
import { client } from '../../../client';
import { dateToIsoStringIgnoringTimezone } from './utils/date-utils';

export interface AppStat {
  id: string;
  name: string;
  icon: string;
  usageTime: string;
  usagePercentage: number;
}

interface UseUserReportParams {
  userId: number | 'All';
  date: Date;
  period: DataPeriod;
  projectId?: number;
  enabled?: boolean;
}

export const useUserReport = ({ userId, date, period, projectId, enabled = true }: UseUserReportParams) => {
  return useQuery<UserReportResponse, AxiosError>({
    queryKey: ['user-report', userId, date.toISOString(), period, projectId],
    queryFn: () =>
      client
        .get('/reports/user-period', {
          params: {
            period: String(period),
            date: dateToIsoStringIgnoringTimezone(date),
            userId,
            ...(projectId && { projectId }),
          },
        })
        .then((res) => res.data),
    enabled: enabled && !!userId,
  });
};

interface UseLatestReportsParams {
  projectId: number;
  enabled?: boolean;
}

export const useLatestReports = ({ projectId, enabled = true }: UseLatestReportsParams) => {
  return useQuery({
    queryKey: ['latest-reports', projectId],
    queryFn: () =>
      client
        .get('/reports/latest', {
          params: {
            projectId,
          },
        })
        .then((res) => res.data),
    enabled: enabled && !!projectId,
  });
};

interface UseAppStatsParams {
  projectId: number;
  range: DataPeriod;
  type: AppStatType;
  enabled?: boolean;
}

export const useAppStats = ({ projectId, range, type, enabled = true }: UseAppStatsParams) => {
  return useQuery<AppStat[]>({
    queryKey: ['app-stats', projectId, range, type],
    queryFn: () =>
      client
        .get('/reports/app-stats', {
          params: {
            projectId,
            range,
            type,
          },
        })
        .then((res) => res.data),
    enabled: enabled && !!projectId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};
